import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileText, Save, Eye, Loader2, ArrowLeft, ImageIcon, Tag, Plus, X, Link2, Sparkles, Upload, Copy
} from 'lucide-react';
import { toast } from 'sonner';

export default function BlogEditorPage() {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [status, setStatus] = useState('draft');
  const [featuredDealIds, setFeaturedDealIds] = useState([]);
  const [dealIdInput, setDealIdInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deals, setDeals] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [galleryUrlInput, setGalleryUrlInput] = useState('');

  useEffect(() => {
    fetchDeals();
    if (isEdit) fetchArticle();
  }, [id]);

  const fetchDeals = async () => {
    try {
      const res = await axios.get(`${API}/deals`, { params: { limit: 50 } });
      setDeals(res.data.deals);
    } catch {}
  };

  const uploadImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/blog/upload-image`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setGalleryImages(prev => [...prev, res.data.url]);
      toast.success('Zdjęcie dodane do galerii!');
    } catch { toast.error('Błąd uploadu'); }
    setImageUploading(false);
    e.target.value = '';
  };

  const uploadImageUrl = async () => {
    if (!galleryUrlInput) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('url', galleryUrlInput);
      const res = await axios.post(`${API}/blog/upload-image-url`, formData, { headers: authHeaders() });
      setGalleryImages(prev => [...prev, res.data.url]);
      setGalleryUrlInput('');
      toast.success('Zdjęcie pobrane i dodane!');
    } catch { toast.error('Nie udało się pobrać zdjęcia'); }
    setImageUploading(false);
  };

  const insertImageToContent = (imgPath) => {
    const markdown = `\n![](${imgPath})\n`;
    setContent(prev => prev + markdown);
    toast.success('Zdjęcie wstawione do treści! Format: ![opis](url)');
  };

  const removeGalleryImage = (idx) => setGalleryImages(prev => prev.filter((_, i) => i !== idx));

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/articles/${id}`);
      const a = res.data;
      setTitle(a.title);
      setSlug(a.slug);
      setContent(a.content);
      setExcerpt(a.excerpt || '');
      setCoverImageUrl(a.cover_image_url || '');
      setTags(a.tags || []);
      setStatus(a.status);
      setFeaturedDealIds(a.featured_deal_ids || []);
    } catch {
      toast.error('Nie znaleziono artykułu');
    }
    setLoading(false);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => setTags(tags.filter(t => t !== tag));

  const toggleDeal = (dealId) => {
    if (featuredDealIds.includes(dealId)) {
      setFeaturedDealIds(featuredDealIds.filter(d => d !== dealId));
    } else {
      setFeaturedDealIds([...featuredDealIds, dealId]);
    }
  };

  const handleSave = async (publishStatus = null) => {
    if (!title || !content) { toast.error('Tytuł i treść są wymagane'); return; }
    setSaving(true);
    try {
      const data = {
        title,
        slug: slug || undefined,
        content,
        excerpt: excerpt || content.slice(0, 200),
        cover_image_url: coverImageUrl || null,
        tags,
        status: publishStatus || status,
        featured_deal_ids: featuredDealIds,
      };

      if (isEdit) {
        await axios.put(`${API}/blog/articles/${id}`, data, { headers: authHeaders() });
        toast.success('Artykuł zaktualizowany!');
      } else {
        const res = await axios.post(`${API}/blog/articles`, data, { headers: authHeaders() });
        toast.success('Artykuł utworzony!');
        navigate(`/admin/blog/edit/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zapisu');
    }
    setSaving(false);
  };

  if (loading) return (
    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="blog-editor-page">
      <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Panel admina
      </button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-white flex items-center gap-2">
          <FileText className="w-6 h-6 text-primary" />
          {isEdit ? 'Edytuj artykuł' : 'Nowy artykuł'}
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="bg-secondary text-secondary-foreground rounded-none text-xs"
            data-testid="save-draft-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> Zapisz szkic
          </Button>
          <Button
            onClick={() => handleSave('published')}
            disabled={saving}
            className="bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none text-xs"
            data-testid="publish-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
            Publikuj
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Title */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">Tytuł artykułu *</Label>
          <Input
            data-testid="article-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Wpisz tytuł artykułu..."
            className="bg-input border-transparent focus:border-primary rounded-none text-lg font-heading"
          />
        </div>

        {/* Slug */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">Slug (URL)</Label>
          <Input
            data-testid="article-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="auto-generowany-z-tytulu"
            className="bg-input border-transparent focus:border-primary rounded-none font-mono text-xs"
          />
        </div>

        {/* Cover Image */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">Zdjęcie okładkowe (URL)</Label>
          <div className="relative">
            <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="article-cover"
              value={coverImageUrl}
              onChange={(e) => setCoverImageUrl(e.target.value)}
              placeholder="https://..."
              className="pl-10 bg-input border-transparent focus:border-primary rounded-none"
            />
          </div>
          {coverImageUrl && (
            <div className="mt-2 h-40 bg-secondary overflow-hidden">
              <img src={coverImageUrl} alt="Cover" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
            </div>
          )}
        </div>

        {/* Excerpt */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">Zajawka (krótki opis)</Label>
          <Textarea
            data-testid="article-excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Krótki opis artykułu (widoczny na liście bloga)..."
            rows={2}
            className="bg-input border-transparent focus:border-primary rounded-none resize-none"
          />
        </div>

        {/* Content */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">
            Treść artykułu * <span className="text-xs text-muted-foreground/50">(## nagłówek, ### podtytuł, - lista, **pogrubienie**)</span>
          </Label>
          <Textarea
            data-testid="article-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Pisz artykuł tutaj...&#10;&#10;## Nagłówek sekcji&#10;Treść sekcji...&#10;&#10;- punkt listy&#10;&#10;**Pogrubiony tekst**&#10;&#10;![opis zdjęcia](/api/uploads/plik.jpg)"
            rows={15}
            className="bg-input border-transparent focus:border-primary rounded-none resize-y font-mono text-sm"
          />
        </div>

        {/* Image Gallery */}
        <div className="bg-card border border-border/40 p-4" data-testid="image-gallery-section">
          <Label className="text-sm text-white font-heading font-bold uppercase tracking-wider mb-3 block flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-primary" /> Galeria zdjęć
          </Label>
          <p className="text-xs text-muted-foreground mb-3">
            Wgraj zdjęcia lub podaj URL. Zdjęcia zostaną zoptymalizowane (800x600) i zapisane na serwerze. Kliknij "Wstaw" aby dodać do treści artykułu.
          </p>

          {/* Upload methods */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <label className="flex-1">
              <div className="flex items-center gap-2 bg-input border border-dashed border-border/60 hover:border-primary/40 p-3 cursor-pointer transition-colors text-center">
                <Upload className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{imageUploading ? 'Wgrywanie...' : 'Wgraj plik'}</span>
              </div>
              <input type="file" accept="image/*" onChange={uploadImageFile} className="hidden" data-testid="gallery-file-upload" />
            </label>
            <div className="flex gap-2 flex-1">
              <Input
                value={galleryUrlInput}
                onChange={(e) => setGalleryUrlInput(e.target.value)}
                placeholder="URL zdjęcia..."
                className="bg-input border-transparent focus:border-primary rounded-none text-xs"
                data-testid="gallery-url-input"
              />
              <Button type="button" onClick={uploadImageUrl} disabled={imageUploading || !galleryUrlInput} className="bg-secondary text-secondary-foreground rounded-none px-3 text-xs">
                {imageUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Pobierz'}
              </Button>
            </div>
          </div>

          {/* Gallery thumbnails */}
          {galleryImages.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="gallery-grid">
              {galleryImages.map((imgPath, idx) => (
                <div key={idx} className="relative group bg-secondary overflow-hidden aspect-[4/3]">
                  <img src={`${BACKEND}${imgPath}`} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button
                      type="button"
                      onClick={() => insertImageToContent(imgPath)}
                      className="bg-primary text-primary-foreground px-2 py-1 text-[10px] font-heading uppercase hover:bg-primary/90"
                      data-testid={`insert-image-${idx}`}
                    >
                      Wstaw
                    </button>
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(idx)}
                      className="bg-destructive text-destructive-foreground px-2 py-1 text-[10px] font-heading uppercase hover:bg-destructive/90"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tags */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">Tagi</Label>
          <div className="flex gap-2 mb-2">
            <Input
              data-testid="tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Dodaj tag..."
              className="bg-input border-transparent focus:border-primary rounded-none flex-1"
            />
            <Button type="button" onClick={addTag} className="bg-secondary text-secondary-foreground rounded-none px-4">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {tags.map(tag => (
              <span key={tag} className="flex items-center gap-1 text-xs px-2 py-1 bg-primary/10 text-primary border border-primary/20">
                <Tag className="w-3 h-3" /> {tag}
                <button onClick={() => removeTag(tag)} className="hover:text-destructive ml-1"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        {/* Featured Deals */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">
            Polecane produkty w artykule ({featuredDealIds.length} wybranych)
          </Label>
          <div className="max-h-48 overflow-y-auto bg-input border border-border/40 p-2 space-y-1">
            {deals.map(deal => (
              <button
                key={deal.id}
                type="button"
                onClick={() => toggleDeal(deal.id)}
                className={`w-full text-left flex items-center gap-2 p-2 text-xs transition-colors ${
                  featuredDealIds.includes(deal.id)
                    ? 'bg-primary/10 text-primary border border-primary/30'
                    : 'text-muted-foreground hover:text-white hover:bg-secondary/50'
                }`}
                data-testid={`toggle-deal-${deal.id}`}
              >
                <span className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center ${
                  featuredDealIds.includes(deal.id) ? 'border-primary bg-primary text-black' : 'border-border'
                }`}>
                  {featuredDealIds.includes(deal.id) && '✓'}
                </span>
                <span className="truncate">{deal.title}</span>
                {deal.deal_price && <span className="text-primary ml-auto flex-shrink-0">{deal.deal_price} zł</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
